import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import logo from "@/assets/logo.png";
import FreeAnalysisView, { FreeAnalysisTier } from "@/components/landing/FreeAnalysisView";
import SignupConversionBlock from "@/components/landing/SignupConversionBlock";
import AuthModal from "@/components/AuthModal";
import {
  CachedEntry,
  FallbackFamily,
  matchCachedAnalysis,
  peekFreeRemaining,
  getRoutingOutcome,
} from "@/lib/freeTier";
import { trackEvent } from "@/lib/analytics";

type NavState = {
  message?: string;
  entry?: CachedEntry | null;
  matchType?: FreeAnalysisTier;
  family?: FallbackFamily | null;
  displayLabel?: string | null;
  articulation?: string | null;
  remaining?: number;
};

const FreeAnalysisResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const navState = (location.state || {}) as NavState;

  const [message, setMessage] = useState<string>(navState.message || search.get("m") || "");
  const [entry, setEntry] = useState<CachedEntry | null>(navState.entry || null);
  const [matchType, setMatchType] = useState<FreeAnalysisTier | null>(navState.matchType || null);
  const [family, setFamily] = useState<FallbackFamily | null>(navState.family || null);
  const [displayLabel, setDisplayLabel] = useState<string | null>(navState.displayLabel || null);
  const [articulation, setArticulation] = useState<string | null>(navState.articulation || null);
  const [remaining, setRemaining] = useState<number>(
    typeof navState.remaining === "number" ? navState.remaining : peekFreeRemaining()
  );
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState(!matchType);

  // If page is hit directly without state, try to recover via the search-param message.
  useEffect(() => {
    if (!message) {
      navigate("/", { replace: true });
      return;
    }
    if (matchType) {
      setLoading(false);
      return;
    }
    (async () => {
      const r = await matchCachedAnalysis(message);
      trackEvent("free_analysis_routed", getRoutingOutcome(r));
      setEntry(r.matchType === "generic" || r.matchType === "weak" ? null : r.entry);
      setMatchType(r.matchType);
      setFamily(r.matchType === "weak" ? r.family : null);
      setDisplayLabel(r.matchType === "weak" ? r.displayLabel : null);
      setArticulation(r.matchType === "weak" ? r.articulation : null);
      setLoading(false);
    })();
  }, [matchType, message, navigate]);

  useEffect(() => {
    if (matchType && message) {
      trackEvent("free_analysis_viewed", {
        message_length: message.length,
        match_type: matchType,
        toxicity_score: entry?.toxicity_score ?? null,
        family: family ?? null,
        display_label: displayLabel ?? null,
        remaining,
      });
    }
  }, [entry, message, matchType, family, displayLabel, remaining]);

  const handleSignup = () => {
    try {
      localStorage.setItem("greyrock_pending_analysis", message);
    } catch {}
    trackEvent("signup_cta_clicked", { source: "free_analysis_result", match_type: matchType });
    setAuthOpen(true);
  };


  if (!message) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-white/40 text-sm font-mono">Redirecting…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white antialiased overflow-x-hidden">
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, rgba(8,51,68,0.55) 0%, rgba(0,0,0,0) 55%)," +
            "linear-gradient(180deg, #050608 0%, #08090c 60%, #000 100%)",
        }}
      />

      <nav className="sticky top-0 z-30 backdrop-blur-md bg-black/60 border-b border-cyan-400/10">
        <div className="max-w-6xl mx-auto px-5 h-12 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={logo}
              alt="GreyRock AI"
              className="w-7 h-7 object-contain drop-shadow-[0_0_12px_rgba(0,242,255,0.3)]"
            />
            <span className="text-sm font-semibold tracking-tight">
              GreyRock<span className="text-cyan-300">AI</span>
            </span>
          </Link>
          <button
            onClick={() => navigate("/login")}
            className="px-3 py-1.5 text-xs md:text-sm text-white/70 hover:text-cyan-300 transition-colors"
          >
            Sign in
          </button>
        </div>
      </nav>

      <main className="px-5 pt-6 pb-16 md:pt-10">
        <h1 className="sr-only">Forensic Analysis Report</h1>
        {loading && (
          <div className="text-center text-white/50 text-sm font-mono">Loading analysis…</div>
        )}
        {!loading && matchType && (
          <>
            <FreeAnalysisView
              message={message}
              entry={entry}
              matchType={matchType}
              family={family}
              displayLabel={displayLabel}
              articulation={articulation}
              remaining={remaining}
            />
            <div className="mt-5 space-y-1.5 max-w-2xl mx-auto">
              <p className="text-[13px] text-white/60 leading-relaxed flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-300/70 mt-0.5 flex-shrink-0" />
                <span>Analyzed privately in your browser — your message is never sent to a server, stored, or seen by us.</span>
              </p>
              <p className="text-[11px] text-white/35 leading-relaxed pl-6">
                Free analysis runs entirely on your device. We record anonymous usage counts only — never your message.
              </p>
            </div>
            <SignupConversionBlock
              onSignup={handleSignup}
              emphasis={remaining <= 0 ? "primary" : "soft"}
            />
            <div className="text-center mt-6">
              <Link to="/" className="text-xs text-white/40 hover:text-cyan-300 transition-colors font-mono uppercase tracking-wider">
                ← Analyze another message
              </Link>
            </div>
          </>
        )}
      </main>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="signup" />
    </div>
  );
};

export default FreeAnalysisResult;
